import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Polyfill ResizeObserver for older browsers (iOS 12, etc.)
if (typeof window !== 'undefined' && !window.ResizeObserver) {
  window.ResizeObserver = class {
    observe() {}
    unobserve() {}
    disconnect() {}
  };
}

// ─── Deferred Sentry init ──────────────────────────────────────────
// Sentry adds ~30-40KB gzip to the critical-path bundle. Loading it
// synchronously blocks FCP/LCP on slow mobile connections (Moto G on
// Slow 4G shaves ~800ms). We chunk-split it via dynamic import and
// fire the init AFTER the browser has finished the first paint, so
// PageSpeed sees the hero content before Sentry's runtime touches
// the main thread. Errors thrown between page load and Sentry init
// are queued by window.onerror (Sentry hooks into it once ready).
const initSentry = () => {
  import(/* webpackChunkName: "sentry" */ "@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: "https://c8a89cf8aa4eb4ff7364978134c8b365@o4510792196161536.ingest.us.sentry.io/4510792211693568",
      sendDefaultPii: true,
      environment: process.env.NODE_ENV || "production",
      integrations: [
        Sentry.breadcrumbsIntegration(),
        Sentry.linkedErrorsIntegration(),
        Sentry.httpContextIntegration(),
      ],
      denyUrls: [
        /\/beacon\.min\.js/i,
        /static\.cloudflareinsights\.com/i,
        /connect\.facebook\.net/i,
        /analytics\.tiktok\.com/i,
        /www\.googletagmanager\.com/i,
        /www\.google-analytics\.com/i,
        /ads\.openai\.com/i,
      ],
      beforeSend(event) {
        const msg = event.exception?.values?.[0]?.value || '';
        if (msg.includes('Object Not Found Matching Id')) return null;
        if (msg.includes('ChunkLoadError') || msg.includes('Loading chunk') || msg.includes('Loading CSS chunk')) {
          return null;
        }
        if (
          msg.includes("Failed to execute 'removeChild' on 'Node'") ||
          msg.includes("Failed to execute 'insertBefore' on 'Node'") ||
          msg.includes('The node to be removed is not a child of this node') ||
          msg.includes('The node before which the new node is to be inserted is not a child of this node')
        ) {
          return null;
        }
        const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const inFrame = (needle) => frames.some(
          (f) => (f.function && f.function.includes(needle)) ||
                 (f.filename && f.filename.includes(needle))
        );
        if (
          msg.includes("window.webkit.messageHandlers") ||
          msg.includes("window.webkit is undefined") ||
          msg.includes("sendDataToNative") ||
          msg.includes("sendPageHideMessage") ||
          msg.includes("hxp-chat-suppression") ||
          msg.includes("IAB unified bridge") ||
          inFrame("sendDataToNative") ||
          inFrame("sendPageHideMessage") ||
          inFrame("hxp-chat-suppression")
        ) {
          return null;
        }
        const type = event.exception?.values?.[0]?.type || '';
        if (type === 'SyntaxError' && /Unexpected token/i.test(msg)) {
          return null;
        }
        return event;
      },
    });
  }).catch(() => { /* Sentry chunk failed — non-critical, keep app running */ });
};

// Schedule Sentry after first paint using requestIdleCallback (or a
// setTimeout fallback for Safari), so the browser has cycles to
// finish LCP before we touch the main thread again.
if (typeof window !== 'undefined') {
  const kick = () => ('requestIdleCallback' in window
    ? window.requestIdleCallback(initSentry, { timeout: 3000 })
    : setTimeout(initSentry, 1500));
  if (document.readyState === 'complete') kick();
  else window.addEventListener('load', kick, { once: true });
}

// ─── Self-healing chunk-load recovery ──────────────────────────────
// When we ship a new build, the user's already-open tab still has the
// PREVIOUS index.html in memory, which references old hashed chunk
// filenames (e.g. /static/css/7067.241415bd.chunk.css). The new deploy
// invalidates those filenames, so the next route transition or CSS load
// blows up with a ChunkLoadError. The fix is to reload the page once —
// the no-cache header on index.html guarantees the fresh HTML pulls
// the new chunk names.
//
// We guard with sessionStorage so a *genuinely broken* deploy doesn't
// reload-loop the user into a coma — we only attempt the auto-reload
// once per session. If it still fails after that, the error surfaces
// normally.
const isChunkError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = (err.message || String(err)) + '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('ChunkLoadError')
  );
};

const tryRecoverFromChunkError = () => {
  try {
    if (sessionStorage.getItem('ff:chunk-reload-attempted') === '1') return false;
    sessionStorage.setItem('ff:chunk-reload-attempted', '1');
  } catch {
    // sessionStorage disabled (privacy mode etc.) — still attempt reload
    // once; the worst case is the user reloads twice on a truly dead
    // deploy, which is no worse than the status quo.
  }
  // Also unregister any service worker that may be holding onto stale
  // chunk references in its cache, so the reload truly pulls fresh
  // assets from the origin.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => {}));
    }).catch(() => {});
  }
  // Slight delay so the unregister call has a chance to fire before
  // the navigation tears down the page.
  setTimeout(() => window.location.reload(), 50);
  return true;
};

window.addEventListener('error', (event) => {
  if (isChunkError(event.error || event)) tryRecoverFromChunkError();
});
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkError(event.reason)) tryRecoverFromChunkError();
});
// Clear the one-shot reload guard on a successful full page load so the
// next deploy gets its own retry budget. We use a small delay to ensure
// the page actually finished mounting (and didn't immediately blow up
// with another chunk error).
window.addEventListener('load', () => {
  setTimeout(() => {
    try { sessionStorage.removeItem('ff:chunk-reload-attempted'); } catch { /* sessionStorage may be disabled — best-effort only */ }
  }, 5000);
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register the service worker on production builds so Fantastic Forms is
// installable as a PWA and benefits from runtime asset caching.
// We deliberately skip registration in development because CRA / Vite dev
// servers can serve stale chunks once the SW is in control.
if (
  'serviceWorker' in navigator &&
  (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost')
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((reg) => {
        // When a new SW takes over, ask it to skip waiting so users get
        // the latest assets on their next navigation.
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              sw.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {
        // Silent — PWA install is a progressive enhancement.
      });
  });
}
